import {isDefined} from 'shared/web';

/**
 * Creates an invalidatesTags function that only invalidates cache on successful requests.
 *
 * @param getTags - Function or array that defines which tags to invalidate on success
 * @returns RTK Query invalidatesTags function that conditionally invalidates
 *
 * @example Simple array of tags
 * ```typescript
 * invalidatesTags: invalidateOnSuccess(['receiveNote', 'deliveryNote'])
 * ```
 *
 * @example Dynamic tags based on query arguments
 * ```typescript
 * invalidatesTags: invalidateOnSuccess((result, queryArg) => [
 *   {type: 'receiveNoteBasket', id: queryArg.receiveNoteId},
 *   {type: 'receiveNote', id: queryArg.receiveNoteId},
 * ])
 * ```
 */
export const invalidateOnSuccess = <TResult = unknown, TQueryArg = unknown>(
  getTags:
    | readonly (string | {type: string; id?: string | number})[]
    | ((
        result: TResult | undefined,
        queryArg: TQueryArg
      ) => readonly (string | {type: string; id?: string | number})[])
): any => {
  return (result: TResult | undefined, error: unknown, queryArg: TQueryArg) => {
    // Only invalidate if there's no error
    if (isDefined(error)) {
      return [];
    }

    // Support both static arrays and dynamic functions
    if (typeof getTags === 'function') {
      return getTags(result, queryArg);
    }

    return getTags;
  };
};
